import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'
import { immer } from 'zustand/middleware/immer'
import { localStorageKeys } from '~/adapter/localstorage/keys'
import type { IndicatedIssueLabel } from '@ulysses-inc/harami_api_client'

type IndicatedIssueLabelsCacheState = {
  // 別会社のキャッシュが残らないよう companyId をキーに保持する
  labelsByCompanyId: Record<number, IndicatedIssueLabel[]>
}

type IndicatedIssueLabelsCacheAction = {
  setLabels: (companyId: number, labels: IndicatedIssueLabel[]) => void
  logoutIndicatedIssueLabelsCache: () => void
}

const initialState: IndicatedIssueLabelsCacheState = {
  labelsByCompanyId: {},
}

/**
 * 指摘ラベル一覧のローカルキャッシュ。
 *
 * オフライン時 / ネットワーク切断時に API 取得が失敗してもラベル候補が表示
 * できるよう、オンラインで取得成功した時点のレスポンスを localStorage に
 * 永続化する。`useQueryIndicatedIssueLabels` がフォールバック用途で参照する。
 */
export const useIndicatedIssueLabelsCacheStore = create<
  IndicatedIssueLabelsCacheState & IndicatedIssueLabelsCacheAction
>()(
  devtools(
    immer(
      persist(
        set => ({
          ...initialState,
          setLabels: (companyId, labels) =>
            set(state => {
              state.labelsByCompanyId[companyId] = labels
            }),
          logoutIndicatedIssueLabelsCache: () =>
            set(state => {
              state.labelsByCompanyId = initialState.labelsByCompanyId
            }),
        }),
        {
          name: localStorageKeys.indicatedIssueLabelsCacheStore,
          version: 0,
        },
      ),
    ),
  ),
)

export const selectCachedIndicatedIssueLabels =
  (companyId: number) =>
  (state: IndicatedIssueLabelsCacheState): IndicatedIssueLabel[] =>
    state.labelsByCompanyId[companyId] ?? []
